import { useMedplum } from '@medplum/react-hooks';
import {
  AppShell,
  Avatar,
  Box,
  Burger,
  Container,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconCheck,
  IconCalendar,
  IconDashboard,
  IconLicense,
  IconLock,
  IconLogout,
  IconSignature,
  IconBug,
  IconMessageCircle,
  IconUser,
  IconUserCircle,
} from '@tabler/icons-react';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { OnboardingSectionId } from './onboardingTypes';
import { useOnboardingPractitioner } from './useOnboardingPractitioner';
import { useOnboardingStatus } from './useOnboardingStatus';
import { useBugReport } from '../../hooks/useBugReport';
import { useProductFeedback } from '../../hooks/useProductFeedback';
import { BugReportModal } from '../../components/BugReportModal';
import { FeedbackModal } from '../../components/FeedbackModal';

interface NavItem {
  icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  label: string;
  href: string;
  sectionId?: OnboardingSectionId;
}

const onboardingNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/onboarding' },
  { icon: IconUser, label: 'Personal Information', href: '/onboarding/personal-info', sectionId: 'personal-info' },
  { icon: IconCalendar, label: 'Availability', href: '/onboarding/availability', sectionId: 'availability' },
  { icon: IconUserCircle, label: 'Profile', href: '/onboarding/profile', sectionId: 'profile' },
  { icon: IconLicense, label: 'Licenses and Education', href: '/onboarding/licenses-education', sectionId: 'licenses-education' },
  { icon: IconSignature, label: 'Attestation', href: '/onboarding/attestation', sectionId: 'attestation' },
];

function isOnboardingNavActive(href: string, pathname: string): boolean {
  if (href === '/onboarding') {
    return pathname === '/onboarding';
  }
  return pathname === href || pathname.startsWith(href + '/');
}

export function OnboardingLayout(): JSX.Element {
  const medplum = useMedplum();
  const profile = useOnboardingPractitioner();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure();
  const [accountMenuOpened, setAccountMenuOpened] = useState(false);
  const onboardingStatus = useOnboardingStatus();
  const bugReport = useBugReport();
  const productFeedback = useProductFeedback();

  const handleSignOut = (): void => {
    if (false) {
      return;
    }
    medplum.signOut();
    navigate('/signin');
  };

  const displayName = profile?.name?.[0]?.text ?? 'Provider';
  const initials = (() => {
    const n = profile?.name?.[0];
    const g = n?.given?.[0]?.[0];
    const f = n?.family?.[0];
    if (g && f) return `${g}${f}`.toUpperCase();
    if (n?.text) return n.text.slice(0, 2).toUpperCase();
    return 'P';
  })();

  return (
    <>
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
      styles={{
        main: {
          backgroundColor: 'var(--unify-bg-warm)',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        },
        header: {
          padding: 0,
          borderBottom: '1px solid var(--mantine-color-gray-5)',
        },
        navbar: {
          backgroundColor: 'var(--unify-bg-sidebar)',
          borderRight: '1px solid var(--mantine-color-gray-5)',
        },
      }}
    >
      <AppShell.Header>
        <Group h="100%" wrap="nowrap" gap={0}>
          <Group
            h="100%"
            px="md"
            gap="sm"
            wrap="nowrap"
            style={{
              backgroundColor: 'var(--unify-bg-sidebar)',
              borderRight: '1px solid var(--mantine-color-gray-5)',
              minWidth: 0,
              width: 250,
              justifyContent: 'center',
            }}
          >
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text size="lg" fw={600} c="gray.9">
              Unify Mental Health
            </Text>
          </Group>

          <Group
            h="100%"
            px="md"
            wrap="nowrap"
            gap={0}
            justify="flex-end"
            style={{ flex: 1, backgroundColor: 'var(--unify-bg-header)', minWidth: 0 }}
          >
            <Group gap="sm" wrap="nowrap">
                {profile && (
                  <Menu
                    position="bottom-end"
                    width={240}
                    shadow="md"
                    opened={accountMenuOpened}
                    onChange={setAccountMenuOpened}
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar
                            radius="xl"
                            size={32}
                            styles={{
                              root: {
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                backgroundColor: 'var(--unify-bg-sidebar)',
                                color: 'var(--mantine-color-gray-8)',
                                border: '1px solid var(--mantine-color-gray-3)',
                                fontSize: '0.75rem',
                              },
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Text size="sm" fw={400} c="gray.8">
                            {displayName}
                          </Text>
                          <IconChevronDown size={14} stroke={2} style={{ color: 'var(--mantine-color-gray-6)' }} />
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown data-bug-report-exclude="">
                      <Menu.Item
                        leftSection={<IconBug size={16} />}
                        onClick={() => {
                          setAccountMenuOpened(false);
                          void bugReport.startReport();
                        }}
                      >
                        Report a bug
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconMessageCircle size={16} />}
                        onClick={() => {
                          setAccountMenuOpened(false);
                          productFeedback.open();
                        }}
                      >
                        Submit feedback
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconLogout size={16} />}
                        color="red"
                        disabled={false}
                        title={false ? 'Logout is disabled in mock Medplum mode' : undefined}
                        onClick={handleSignOut}
                      >
                        Logout
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <AppShell.Section grow component={ScrollArea}>
          {onboardingNavItems.map((item) => {
            const locked = item.sectionId ? !onboardingStatus.unlockedSections[item.sectionId] : false;
            const complete = item.sectionId ? onboardingStatus.sections[item.sectionId].complete : false;
            const leftSection = complete
              ? <IconCheck size={18} stroke={1.8} />
              : locked
                ? <IconLock size={18} stroke={1.5} />
                : <item.icon size={20} stroke={1.5} />;

            const link = (
              <NavLink
                key={item.href}
                href={item.href}
                label={item.label}
                leftSection={leftSection}
                active={!locked && isOnboardingNavActive(item.href, location.pathname)}
                childrenOffset={0}
                onClick={(e) => {
                  e.preventDefault();
                  if (!locked) {
                    navigate(item.href);
                    close();
                  }
                }}
                mb={4}
                styles={locked ? { root: { opacity: 0.45, cursor: 'not-allowed' } } : undefined}
              />
            );

            return locked ? (
              <Tooltip key={item.href} label="Complete the previous step to unlock" position="right" withArrow>
                <Box>{link}</Box>
              </Tooltip>
            ) : (
              link
            );
          })}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box w="100%" style={{ minWidth: 0 }}>
          <Container size="xl">
            <Outlet />
          </Container>
        </Box>
      </AppShell.Main>
    </AppShell>
      <BugReportModal
        opened={bugReport.opened}
        capturing={bugReport.capturing}
        screenshotBase64={bugReport.screenshotBase64}
        submitting={bugReport.submitting}
        onClose={bugReport.onClose}
        onSubmit={(values) => void bugReport.onSubmit(values)}
        onScreenshotChange={bugReport.onScreenshotChange}
      />
      <FeedbackModal
        opened={productFeedback.opened}
        submitting={productFeedback.submitting}
        onClose={productFeedback.onClose}
        onSubmit={(values) => void productFeedback.onSubmit(values)}
      />
    </>
  );
}
